/**
 * 보안 파일 미리보기 컴포넌트
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button } from '../UI';
import { useSecureFiles } from '../../contexts/SecureFilesContext';
import { Download, X, AlertCircle } from 'lucide-react';

function SecureFilePreview({ file, isOpen, onClose }) {
  const { previewFile, downloadFile } = useSecureFiles();
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && file) {
      loadPreview();
    }

    return () => {
      // 메모리 정리
      if (previewData?.cleanup) {
        previewData.cleanup();
      }
    };
  }, [isOpen, file]);

  const loadPreview = async () => {
    setLoading(true);
    setError('');

    try {
      const preview = await previewFile(file.id);
      setPreviewData(preview);
    } catch (err) {
      setError(err.message || '미리보기를 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      await downloadFile(file.id);
    } catch (err) {
      setError(err.message || '다운로드에 실패했습니다.');
    }
  };

  const renderPreview = () => {
    if (loading) {
      return (
        <div className="preview-loading">
          미리보기를 불러오는 중...
        </div>
      );
    }

    if (error) {
      return (
        <div className="preview-error">
          <AlertCircle size={48} />
          <p>{error}</p>
        </div>
      );
    }

    if (!previewData) {
      return null;
    }

    const { url, mimeType } = previewData;

    // 이미지 미리보기
    if (mimeType.startsWith('image/')) {
      return (
        <div className="preview-image">
          <img src={url} alt={file.originalName} />
        </div>
      );
    }

    // PDF 미리보기
    if (mimeType.includes('pdf')) {
      return (
        <div className="preview-pdf">
          <iframe
            src={url}
            title={file.originalName}
            width="100%"
            height="600px"
          />
        </div>
      );
    }

    // 텍스트 파일 미리보기
    if (mimeType.startsWith('text/')) {
      return (
        <div className="preview-text">
          <iframe
            src={url}
            title={file.originalName}
            width="100%"
            height="600px"
          />
        </div>
      );
    }

    return (
      <div className="preview-unsupported">
        <p>이 파일 형식은 미리보기를 지원하지 않습니다.</p>
        <Button
          variant="primary"
          icon={<Download size={18} />}
          onClick={handleDownload}
        >
          다운로드
        </Button>
      </div>
    );
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={file?.originalName || '파일 미리보기'}
      size="lg"
    >
      <div className="secure-file-preview">
        {renderPreview()}
        
        <div className="preview-actions">
          <Button variant="ghost" onClick={onClose}>
            닫기
          </Button>
          <Button
            variant="primary"
            icon={<Download size={18} />}
            onClick={handleDownload}
          >
            다운로드
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default SecureFilePreview;